<template>
  <div>
    <!-- Dialog de Histórico (lista de metas) — oculto no modo openDirectAdd -->
    <v-dialog
      v-if="!openDirectAdd"
      v-model="internalValue"
      transition="dialog-bottom-transition"
      max-width="1000px"
      scrollable
    >
      <v-card class="ds-dialog">
        <v-card-title
          class="ds-dialog-header d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-flag-checkered</v-icon>
            <span class="ds-dialog-title">{{ dialogTitle }}</span>
          </div>
          <div class="d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="white"
                  @click="openAdd"
                  class="mr-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>Nova meta</span>
            </v-tooltip>
            <v-btn icon color="white" class="close-btn" @click="close">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section v-if="goals.length" class="ds-form-section ds-form-section--flush">
            <v-simple-table class="history-table">
              <thead>
                <tr>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4">
                    Meta
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4">
                    Tipo
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 140px">
                    Status
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 130px">
                    Alvo
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 90px">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in goals" :key="item.id" class="history-row">
                  <td class="pa-4">
                    <span class="body-2 font-weight-medium">{{ item.goal_name }}</span>
                  </td>
                  <td class="pa-4">
                    <span class="body-2">{{ item.goal_type || "—" }}</span>
                  </td>
                  <td class="pa-4">
                    <v-chip
                      x-small
                      :color="statusColor(item.status)"
                      text-color="white"
                    >
                      {{ item.status }}
                    </v-chip>
                  </td>
                  <td class="text-no-wrap pa-4">
                    <span class="body-2">{{ formatDate(item.target_date) }}</span>
                  </td>
                  <td class="pa-4">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="primary"
                          class="action-btn mr-1"
                          @click="openLinker(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-link-variant</v-icon>
                        </v-btn>
                      </template>
                      <span>Vincular</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="catMint"
                          class="action-btn mr-1"
                          @click="openEdit(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Editar</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="error"
                          class="action-btn"
                          @click="confirmDelete(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Excluir</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <!-- Estado vazio -->
          <section v-else class="ds-form-section text-center py-8 px-4">
            <v-icon size="56" color="grey lighten-1">mdi-flag-outline</v-icon>
            <p class="body-1 grey--text mt-3 mb-0">Nenhuma meta registrada.</p>
            <v-btn color="catMint" outlined small class="mt-4" @click="openAdd">
              <v-icon small left>mdi-plus</v-icon>
              Adicionar primeira meta
            </v-btn>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Form de meta (mesmo da aba Metas) -->
    <GoalFormDialog
      v-model="formOpen"
      :patient-id="patientId"
      :goal="editingGoal"
      @saved="onSaved"
    />

    <!-- Vincular meta ↔ qualquer coisa do paciente (correlação universal) -->
    <CorrelationLinker
      v-model="linkerOpen"
      source-type="goal"
      :source-id="linkerGoal ? linkerGoal.id : null"
      :source-label="linkerGoal ? linkerGoal.goal_name : ''"
      :patient-id="patientId"
      @changed="$emit('data-saved')"
    />
  </div>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import GoalFormDialog from "@/features/patients/components/GoalFormDialog.vue";
import CorrelationLinker from "@/features/patients/profile-dashboard/shared/CorrelationLinker.vue";
import { formatDate } from "@/utils/datetime";

export default {
  name: "EditGoalsDialog",

  components: { GoalFormDialog, CorrelationLinker },

  props: {
    value: { type: Boolean, default: false },
    dialogTitle: { type: String, default: "Metas e Prazos do paciente" },
    goals: { type: Array, default: () => [] },
    patientId: { type: [String, Number], required: true },
    openDirectAdd: { type: Boolean, default: false },
  },

  setup() {
    const { success, error, erroDeRequisicao } = useSnackbar();
    return { success, error, erroDeRequisicao };
  },

  data() {
    return {
      formOpen: false,
      editingGoal: null,
      linkerOpen: false,
      linkerGoal: null,
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },

  watch: {
    value(val) {
      if (val && this.openDirectAdd) {
        this.$nextTick(() => this.openAdd());
      }
    },
    formOpen(val) {
      if (!val && this.openDirectAdd) {
        this.$emit("input", false);
      }
    },
  },

  methods: {
    formatDate: (v) => formatDate(v, { fallback: "—" }),
    statusColor(status) {
      return (
        {
          "Concluído": "green darken-1",
          "Em Andamento": "blue darken-1",
          "Não Iniciado": "grey darken-1",
          "Cancelado": "red darken-1",
        }[status] || "grey"
      );
    },

    openAdd() {
      this.editingGoal = null;
      this.formOpen = true;
    },

    openEdit(goal) {
      this.editingGoal = { ...goal };
      this.formOpen = true;
    },

    openLinker(goal) {
      this.linkerGoal = goal;
      this.linkerOpen = true;
    },

    onSaved(registrado) {
      this.$emit("data-saved", registrado);
    },

    async confirmDelete(goal) {
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar exclusão",
        message: `Tem certeza que deseja remover a meta "${goal.goal_name}"?`,
        hint: "Esta ação não poderá ser desfeita.",
      });
      if (!ok) return;
      try {
        await http.delete(`/api/patient-goals/delete/${goal.id}`);
        this.success("Meta removida com sucesso!");
        this.$emit("data-deleted");
      } catch (err) {
        console.error("Erro ao excluir meta:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao remover a meta");
      }
    },

    close() {
      this.internalValue = false;
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.ds-dialog-header {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%);
  color: var(--ds-white);
}

.ds-dialog-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.ds-dialog-header :deep(.v-btn) {
  position: relative;
  z-index: 1;
}

.ds-dialog-header :deep(.v-btn:hover) {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.history-table thead th {
  background-color: var(--ds-primary-surface) !important;
  border-bottom: 2px solid var(--ds-primary-border) !important;
  letter-spacing: 0.4px;
  color: var(--ds-primary-deep) !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
}

.history-row {
  transition: background-color 0.15s ease;
}

.history-row:hover {
  background-color: var(--ds-primary-surface) !important;
}

.history-row td {
  border-bottom: 1px solid var(--ds-primary-border) !important;
  color: var(--ds-text-600);
}

.action-btn {
  transition: transform 0.15s ease, background-color 0.15s ease;
}

.action-btn:hover {
  transform: scale(1.15);
}
</style>
